/**
 * Board themes.
 *
 * The palettes come from the house theme collection (xiangqi-theme-collection.html)
 * and are transcribed here unchanged — board frame and surface, grid, palace,
 * river and point markers, plus a full piece spec per side: a three-stop bezel,
 * a two-stop face, the glyph and its lift, and the rivet colour.
 *
 * A theme also owns the *geometry* of the grid. The original walnut board is a
 * photograph, so its grid sits where the artwork put it — measured, irregular,
 * and occupying only ~61% of the frame's width, because the rest is carved
 * border. Every drawn theme rules its own lines and gives the grid ~84% of the
 * same space, which makes the pieces about a third larger on screen at
 * identical container size, with no image to download at all.
 *
 * Adding a theme means adding an entry. Nothing else needs to change.
 */

import type { Bi } from "./lessons.js";

export type ThemeKind = "photo" | "drawn";

/** One side's pieces. `engrave` cuts the glyph in rather than embossing it out. */
export interface PieceSpec {
  bezelHi: string;
  bezelMid: string;
  bezelLo: string;
  faceHi: string;
  faceLo: string;
  glyph: string;
  glyphLift: string;
  rivet: string;
  engrave: boolean;
}

export interface BoardSpec {
  frameHi: string;
  frameLo: string;
  surfHi: string;
  surfLo: string;
  line: string;
  palace: string;
  river: string;
  marker: string;
}

export interface BoardTheme {
  id: string;
  name: Bi;
  /** Why this theme exists, shown under the picker. */
  note?: string;
  kind: ThemeKind;
  /** X of files a–i, and Y of ranks 9→0 (top to bottom), in viewBox units. */
  fileX: number[];
  rankY: number[];
  pr: number;
  fontSize: number;
  gutL: number;
  gutB: number;
  board?: BoardSpec;
  red?: PieceSpec;
  blk?: PieceSpec;
  /** The photo theme's carved gold characters. */
  goldGlyph?: boolean;
}

const VB = 1000;

/**
 * The photographic board. These numbers are measured from the artwork — the
 * river band is taller than the other cells because the render draws it that
 * way — so they cannot be computed and must not be "tidied up".
 */
const PHOTO_FILE_X = [178.6, 257.6, 338.9, 418.7, 499.2, 578.2, 658.7, 739.2, 819.0];
const PHOTO_RANK_Y = [95.7, 171.5, 249.6, 328.5, 408.3, 545.5, 624.4, 702.6, 781.5, 864.4];

/**
 * A drawn grid, evenly spaced — which is how a real board is ruled; the river is
 * simply an unmarked band the same height as every other rank.
 */
function drawnGrid(margin: number, top: number) {
  const span = VB - margin * 2;
  const gx = span / 8;
  const fileX = Array.from({ length: 9 }, (_, i) => margin + i * gx);
  const height = VB - top * 2;
  const gy = height / 9;
  const rankY = Array.from({ length: 10 }, (_, i) => top + i * gy);
  return { fileX, rankY, spacing: Math.min(gx, gy) };
}

// 80/72, not 72/58: at 58 the top and bottom rows of pieces touched the frame
// (the pieces are 0.455 of a cell across their radius), which players read as a
// cramped, unfinished board. A 2% smaller cell buys a clear margin all round.
const DRAWN = drawnGrid(80, 72);
const DRAWN_PR = Math.round(DRAWN.spacing * 0.455);
// Glyph size is set against the *face* (the area inside the bezel), not the
// outer disc: at 0.90r the character filled barely half of it and read as a
// small mark on a big counter rather than a piece.
const DRAWN_FS = Math.round(DRAWN_PR * 1.15);

/** Board + pieces from the collection, on the drawn grid. */
const COLLECTION: {
  id: string; name: Bi; note: string; board: BoardSpec; red: PieceSpec; blk: PieceSpec;
}[] = [
  {
    id: "forbidden",
    name: { zh: "紫禁城", en: "Forbidden City", vi: "Tử Cấm Thành" },
    note: "Imperial vermilion under gold. The loudest theme in the collection and the one that sells the app in a screenshot. Cinnabar lacquer board, gold leaf grid, everything turned up.",
    board: { frameHi: "#E8C46A", frameLo: "#6B4A0E", surfHi: "#8E2B1F", surfLo: "#3D0E0A", line: "#E2BE6E", palace: "#F0D48C", river: "#F3DDA0", marker: "#E2BE6E" },
    red: { bezelHi: "#FFF3C4", bezelMid: "#C9A227", bezelLo: "#5E430A", faceHi: "#B33223", faceLo: "#4A0F0B", glyph: "#FCE9B4", glyphLift: "#2E0705", rivet: "#F2D77A", engrave: true },
    blk: { bezelHi: "#FFF3C4", bezelMid: "#C9A227", bezelLo: "#5E430A", faceHi: "#33302C", faceLo: "#0C0B0A", glyph: "#F7E3AE", glyphLift: "#000000", rivet: "#F2D77A", engrave: true },
  },
  {
    id: "jade",
    name: { zh: "玉皇", en: "Jade Emperor", vi: "Ngọc Hoàng" },
    note: "Translucent stone in gold claw settings on a pale celadon slab. The only theme where the two sides differ in material rather than in tone, which is how real high end sets are cut.",
    board: { frameHi: "#FFEFB4", frameLo: "#7A5A10", surfHi: "#E6EDE0", surfLo: "#AFC0AC", line: "#4A6152", palace: "#2F4A3C", river: "#2F4A3C", marker: "#4A6152" },
    red: { bezelHi: "#FFF0B8", bezelMid: "#C9A227", bezelLo: "#7A5A10", faceHi: "#F6DECD", faceLo: "#D9A18A", glyph: "#5E1D12", glyphLift: "#FFEEE2", rivet: "#EFD07A", engrave: true },
    blk: { bezelHi: "#FFF0B8", bezelMid: "#C9A227", bezelLo: "#7A5A10", faceHi: "#C6EAD6", faceLo: "#7CC0A2", glyph: "#083327", glyphLift: "#DEF7EA", rivet: "#EFD07A", engrave: true },
  },
  {
    id: "huanghuali",
    name: { zh: "黃花梨", en: "Huanghuali Study", vi: "Thư phòng gỗ sưa" },
    note: "Mirror turned steel against oiled yellow rosewood. The quietest of the traditional group and the one people will actually play on for two hours without eye strain.",
    board: { frameHi: "#C9A876", frameLo: "#4A331B", surfHi: "#C99B5E", surfLo: "#8A5A2C", line: "#4A2F16", palace: "#3A2410", river: "#3A2410", marker: "#4A2F16" },
    red: { bezelHi: "#FAFBFC", bezelMid: "#A8B0B6", bezelLo: "#454C52", faceHi: "#A0512F", faceLo: "#53201A", glyph: "#F8EBCC", glyphLift: "#37130E", rivet: "#D7DDE1", engrave: true },
    blk: { bezelHi: "#FAFBFC", bezelMid: "#A8B0B6", bezelLo: "#454C52", faceHi: "#443C35", faceLo: "#16120F", glyph: "#F0E8D9", glyphLift: "#000000", rivet: "#D7DDE1", engrave: true },
  },
  {
    id: "song",
    name: { zh: "宋墨", en: "Song Ink Wash", vi: "Thuỷ mặc đời Tống" },
    note: "Aged xuan paper with a pewter rim. Almost no colour anywhere, so the whole theme lives on paper texture and the weight of the brush stroke. Scholarly rather than showy.",
    board: { frameHi: "#D8CFBC", frameLo: "#514839", surfHi: "#F4EEE0", surfLo: "#D8CDB6", line: "#6A6252", palace: "#4A4436", river: "#4A4436", marker: "#6A6252" },
    red: { bezelHi: "#F0F0EE", bezelMid: "#9E9C94", bezelLo: "#4C4A44", faceHi: "#FBF6EA", faceLo: "#DCD0B8", glyph: "#9C2F1E", glyphLift: "#FFFFFF", rivet: "#CFCDC5", engrave: true },
    blk: { bezelHi: "#F0F0EE", bezelMid: "#9E9C94", bezelLo: "#4C4A44", faceHi: "#FBF6EA", faceLo: "#D2C8B2", glyph: "#211E19", glyphLift: "#FFFFFF", rivet: "#CFCDC5", engrave: true },
  },
  {
    id: "qinghua",
    name: { zh: "青花", en: "Blue and White Porcelain", vi: "Sứ thanh hoa" },
    note: "Cobalt underglaze on kiln white, rimmed in gold. Fragile looking on purpose. The lightest board in the set and the easiest on a bright screen outdoors.",
    board: { frameHi: "#FFEFB4", frameLo: "#7A5A10", surfHi: "#FBFAF6", surfLo: "#DFE6EE", line: "#2B5A96", palace: "#17406F", river: "#17406F", marker: "#2B5A96" },
    red: { bezelHi: "#FFF0B8", bezelMid: "#C9A227", bezelLo: "#7A5A10", faceHi: "#FDFCFA", faceLo: "#DDD4CA", glyph: "#9C2A1C", glyphLift: "#FFFFFF", rivet: "#EFD07A", engrave: true },
    blk: { bezelHi: "#FFF0B8", bezelMid: "#C9A227", bezelLo: "#7A5A10", faceHi: "#FBFCFE", faceLo: "#C9D8E8", glyph: "#123C6E", glyphLift: "#FFFFFF", rivet: "#EFD07A", engrave: true },
  },
  {
    id: "lantern",
    name: { zh: "紅燈籠", en: "Red Lantern", vi: "Đèn lồng đỏ" },
    note: "Brass on black piano lacquer, with the pieces themselves in deep festival red. Highest contrast board in the collection, and the glyphs are embossed rather than cut so they sit proud.",
    board: { frameHi: "#E4C07C", frameLo: "#5A4110", surfHi: "#2A2422", surfLo: "#0B0908", line: "#B08A3C", palace: "#D4A94F", river: "#D9B360", marker: "#B08A3C" },
    red: { bezelHi: "#F7DC9E", bezelMid: "#B08A3C", bezelLo: "#4A340C", faceHi: "#A32218", faceLo: "#3E0A07", glyph: "#F9DC98", glyphLift: "#260504", rivet: "#E0C079", engrave: false },
    blk: { bezelHi: "#F7DC9E", bezelMid: "#B08A3C", bezelLo: "#4A340C", faceHi: "#26231F", faceLo: "#080706", glyph: "#F9DC98", glyphLift: "#000000", rivet: "#E0C079", engrave: false },
  },
  {
    id: "ivory",
    name: { zh: "象牙閣", en: "Ivory Pavilion", vi: "Lầu ngà" },
    note: "Bone white faces in a warm walnut frame. Reads as a daytime, well lit table rather than a lamp lit study. Gentlest theme here and a good default for a light interface.",
    board: { frameHi: "#C7A379", frameLo: "#4E3620", surfHi: "#E9D9BE", surfLo: "#C4A67F", line: "#6A4C2E", palace: "#503820", river: "#503820", marker: "#6A4C2E" },
    red: { bezelHi: "#F6E6CE", bezelMid: "#B4906A", bezelLo: "#54391E", faceHi: "#FBF3E2", faceLo: "#D9C6A6", glyph: "#93301F", glyphLift: "#FFFFFF", rivet: "#E0C8A4", engrave: true },
    blk: { bezelHi: "#F6E6CE", bezelMid: "#B4906A", bezelLo: "#54391E", faceHi: "#F4EADA", faceLo: "#C3B398", glyph: "#26211A", glyphLift: "#FFFFFF", rivet: "#E0C8A4", engrave: true },
  },
  {
    id: "pearl",
    name: { zh: "珠貝", en: "Obsidian and Pearl", vi: "Hắc diện thạch và ngọc trai" },
    note: "Nacre faces set in rose gold on black volcanic stone. The pieces glow against the board without either side being loud. Softest premium option in the collection.",
    board: { frameHi: "#F0C0AC", frameLo: "#5E3229", surfHi: "#26211F", surfLo: "#0A0808", line: "#B07A6A", palace: "#D09A88", river: "#D8A896", marker: "#B07A6A" },
    red: { bezelHi: "#FFE3D6", bezelMid: "#C9857A", bezelLo: "#6E3A32", faceHi: "#FCF3EC", faceLo: "#DCC0B4", glyph: "#7E2A20", glyphLift: "#FFFFFF", rivet: "#F0BCA8", engrave: true },
    blk: { bezelHi: "#FFE3D6", bezelMid: "#C9857A", bezelLo: "#6E3A32", faceHi: "#EFEBE6", faceLo: "#ADA69E", glyph: "#211D1A", glyphLift: "#FFFFFF", rivet: "#F0BCA8", engrave: true },
  },
  {
    id: "bronze",
    name: { zh: "青銅", en: "Dragon Bronze", vi: "Rồng đồng" },
    note: "Patinated ritual bronze on slate, with the black side gone to verdigris. Aged rather than shiny, and the only theme where the two sides read as two different alloys.",
    board: { frameHi: "#B99A62", frameLo: "#3A2C14", surfHi: "#4C4E4A", surfLo: "#1E201E", line: "#9A8248", palace: "#B89A58", river: "#C0A462", marker: "#9A8248" },
    red: { bezelHi: "#E8C98F", bezelMid: "#9A7238", bezelLo: "#3E2C13", faceHi: "#A8783E", faceLo: "#4A3016", glyph: "#FCEDC8", glyphLift: "#291908", rivet: "#C9A468", engrave: true },
    blk: { bezelHi: "#BCC8B8", bezelMid: "#5E7060", bezelLo: "#212B23", faceHi: "#50604F", faceLo: "#19211A", glyph: "#DEEBDA", glyphLift: "#080D09", rivet: "#93A392", engrave: true },
  },
  {
    id: "persimmon",
    name: { zh: "秋柿", en: "Autumn Persimmon", vi: "Hồng mùa thu" },
    note: "Copper and amber over warm ochre. The most seasonal theme, and the one that photographs best in a light interface without going pale. Copper rims patina toward the outer edge.",
    board: { frameHi: "#E0A874", frameLo: "#5A3316", surfHi: "#D8A45E", surfLo: "#9A5A24", line: "#5A3316", palace: "#42230D", river: "#42230D", marker: "#5A3316" },
    red: { bezelHi: "#FBD9A8", bezelMid: "#C07840", bezelLo: "#4E2A10", faceHi: "#C4551F", faceLo: "#5A1E0A", glyph: "#FFE6BE", glyphLift: "#3A1105", rivet: "#E8B57C", engrave: true },
    blk: { bezelHi: "#FBD9A8", bezelMid: "#C07840", bezelLo: "#4E2A10", faceHi: "#4A3A2C", faceLo: "#1A120C", glyph: "#F6E2C6", glyphLift: "#000000", rivet: "#E8B57C", engrave: true },
  },
  {
    id: "sapphire",
    name: { zh: "藍寶", en: "Sapphire Court", vi: "Điện lam ngọc" },
    note: "Platinum on deep sapphire velvet. The red side stays warm so it does not get swallowed by the blue ground. Formal, cold, and the best fit for a dark interface that is not trying to look technical.",
    board: { frameHi: "#DDE4EC", frameLo: "#3A424E", surfHi: "#1E3358", surfLo: "#080F20", line: "#7E93B4", palace: "#9EB2CE", river: "#AEC0D8", marker: "#7E93B4" },
    red: { bezelHi: "#F4F7FA", bezelMid: "#AEB8C4", bezelLo: "#4A5460", faceHi: "#A83A2C", faceLo: "#440E0A", glyph: "#FCE6D4", glyphLift: "#2A0705", rivet: "#DCE4EC", engrave: true },
    blk: { bezelHi: "#F4F7FA", bezelMid: "#AEB8C4", bezelLo: "#4A5460", faceHi: "#243C68", faceLo: "#080F22", glyph: "#DCE8FA", glyphLift: "#000510", rivet: "#DCE4EC", engrave: true },
  },
  {
    id: "titanium",
    name: { zh: "鈦夜", en: "Midnight Titanium", vi: "Titan nửa đêm" },
    note: "Anodised gunmetal on carbon, with the glyphs lit rather than painted. This is the one that solves the dark interface problem: the second side is cold blue, not black, so it never disappears into the board.",
    board: { frameHi: "#C0C8CE", frameLo: "#22282C", surfHi: "#1C2024", surfLo: "#07090B", line: "#48525A", palace: "#5FB0F5", river: "#6E7A84", marker: "#48525A" },
    red: { bezelHi: "#C7CDD2", bezelMid: "#6A7278", bezelLo: "#23282C", faceHi: "#2C3034", faceLo: "#0C0E10", glyph: "#FF6E5C", glyphLift: "#000000", rivet: "#9BA3A9", engrave: false },
    blk: { bezelHi: "#C7CDD2", bezelMid: "#6A7278", bezelLo: "#23282C", faceHi: "#2C3034", faceLo: "#0C0E10", glyph: "#5FB0F5", glyphLift: "#000000", rivet: "#9BA3A9", engrave: false },
  },  // ---- later additions ----------------------------------------------------
  // Same format as everything above: a board palette, then a piece spec per
  // side. The last two are not decorative -- see their notes.
  {
    id: "bamboo",
    name: { zh: "竹林", en: "Bamboo Grove", vi: "Rừng trúc" },
    note: "Split bamboo over rice paper. The lightest board here, and the easiest on the eyes for a long game.",
    board: { frameHi: "#BFA76A", frameLo: "#6B5A2C", surfHi: "#F4EFD9", surfLo: "#DED3AE", line: "#5F7A47", palace: "#4C6638", river: "#7E9668", marker: "#5F7A47" },
    red: { bezelHi: "#F6EFD2", bezelMid: "#B9A465", bezelLo: "#584A22", faceHi: "#FBF6E6", faceLo: "#E3D6AE", glyph: "#9B2B1B", glyphLift: "#FFFFFF", rivet: "#D8C68A", engrave: true },
    blk: { bezelHi: "#F6EFD2", bezelMid: "#B9A465", bezelLo: "#584A22", faceHi: "#EAF0E2", faceLo: "#B7C7A8", glyph: "#26431C", glyphLift: "#FFFFFF", rivet: "#D8C68A", engrave: true },
  },
  {
    id: "inkstone",
    name: { zh: "硯台", en: "Inkstone", vi: "Nghiên mực" },
    note: "A scholar's slate inkstone: one stone, one colour, and the characters the only bright thing on the desk.",
    board: { frameHi: "#7C828A", frameLo: "#2A2E33", surfHi: "#4A5058", surfLo: "#23272C", line: "#9AA3AD", palace: "#B3BCC6", river: "#B3BCC6", marker: "#9AA3AD" },
    red: { bezelHi: "#B9C0C8", bezelMid: "#6C737B", bezelLo: "#22262A", faceHi: "#3A3F45", faceLo: "#181B1F", glyph: "#FF7A66", glyphLift: "#000000", rivet: "#98A1AA", engrave: false },
    blk: { bezelHi: "#B9C0C8", bezelMid: "#6C737B", bezelLo: "#22262A", faceHi: "#3A3F45", faceLo: "#181B1F", glyph: "#E8EEF5", glyphLift: "#000000", rivet: "#98A1AA", engrave: false },
  },
  {
    id: "ebony",
    name: { zh: "烏木", en: "Ebony and Gold", vi: "Gỗ mun và vàng" },
    note: "Black hardwood with the grid inlaid in gold wire. Formal, and very dark -- the pieces do all the talking.",
    board: { frameHi: "#D8B564", frameLo: "#4A3A14", surfHi: "#2A2621", surfLo: "#100E0B", line: "#C9A227", palace: "#E3C577", river: "#E3C577", marker: "#C9A227" },
    red: { bezelHi: "#FBE8AE", bezelMid: "#C9A227", bezelLo: "#503A08", faceHi: "#A82A1C", faceLo: "#3E0C08", glyph: "#FCEBBC", glyphLift: "#240604", rivet: "#EDD285", engrave: true },
    blk: { bezelHi: "#FBE8AE", bezelMid: "#C9A227", bezelLo: "#503A08", faceHi: "#1E1B18", faceLo: "#070605", glyph: "#F6E4AC", glyphLift: "#000000", rivet: "#EDD285", engrave: true },
  },
  {
    id: "dunhuang",
    name: { zh: "敦煌", en: "Dunhuang Fresco", vi: "Bích hoạ Đôn Hoàng" },
    note: "Cave-wall pigment: an ochre ground, mineral blue, and a red that has been fading for a thousand years.",
    board: { frameHi: "#C9A56C", frameLo: "#5E441F", surfHi: "#E3C79A", surfLo: "#BE9C6A", line: "#7A4B2A", palace: "#2E5A79", river: "#2E5A79", marker: "#7A4B2A" },
    red: { bezelHi: "#EFD6AC", bezelMid: "#A9834E", bezelLo: "#4C3418", faceHi: "#C05A3C", faceLo: "#6E2418", glyph: "#F7E4C2", glyphLift: "#3A0F08", rivet: "#D9BC8C", engrave: true },
    blk: { bezelHi: "#EFD6AC", bezelMid: "#A9834E", bezelLo: "#4C3418", faceHi: "#39587A", faceLo: "#15263A", glyph: "#E6EEF6", glyphLift: "#0A1520", rivet: "#D9BC8C", engrave: true },
  },
  {
    id: "cloisonne",
    name: { zh: "景泰藍", en: "Cloisonné", vi: "Pháp lam" },
    note: "Turquoise enamel held in gilt wire, the way a Jingtai vase is made. The brightest blue in the collection.",
    board: { frameHi: "#F0D486", frameLo: "#6E5312", surfHi: "#1E6E78", surfLo: "#0B3B44", line: "#E6C97A", palace: "#F4E0A4", river: "#F4E0A4", marker: "#E6C97A" },
    red: { bezelHi: "#FBEBBA", bezelMid: "#C9A227", bezelLo: "#57400C", faceHi: "#B23A2A", faceLo: "#4A1009", glyph: "#FBEBC4", glyphLift: "#2E0705", rivet: "#EFD489", engrave: true },
    blk: { bezelHi: "#FBEBBA", bezelMid: "#C9A227", bezelLo: "#57400C", faceHi: "#17505C", faceLo: "#06202A", glyph: "#DFF3F6", glyphLift: "#031218", rivet: "#EFD489", engrave: true },
  },
  {
    id: "sandalwood",
    name: { zh: "檀香", en: "Sandalwood", vi: "Gỗ đàn hương" },
    note: "Pale warm wood with a close grain. Quiet, and the closest thing here to a real set on a real table.",
    board: { frameHi: "#D9B383", frameLo: "#77502C", surfHi: "#EBCEA6", surfLo: "#CFA877", line: "#7A5230", palace: "#8E6339", river: "#9C734A", marker: "#7A5230" },
    red: { bezelHi: "#F3DCBC", bezelMid: "#B08A5E", bezelLo: "#50351C", faceHi: "#FAF0DE", faceLo: "#E0C8A4", glyph: "#A03020", glyphLift: "#FFFFFF", rivet: "#DEC29A", engrave: true },
    blk: { bezelHi: "#F3DCBC", bezelMid: "#B08A5E", bezelLo: "#50351C", faceHi: "#F0E2CC", faceLo: "#C9B08C", glyph: "#2C241A", glyphLift: "#FFFFFF", rivet: "#DEC29A", engrave: true },
  },
  {
    id: "snowplum",
    name: { zh: "雪梅", en: "Snow Plum", vi: "Mai tuyết" },
    note: "Plum blossom against snow: an ice-white board, and the only warm thing on it is the red.",
    board: { frameHi: "#D8E2EC", frameLo: "#7F8C9A", surfHi: "#FAFCFE", surfLo: "#DDE7F0", line: "#8A99A8", palace: "#A63A52", river: "#A63A52", marker: "#8A99A8" },
    red: { bezelHi: "#FFFFFF", bezelMid: "#AEBDCB", bezelLo: "#4E5A66", faceHi: "#C24360", faceLo: "#5E1428", glyph: "#FFF0F4", glyphLift: "#3A0A17", rivet: "#D6E1EB", engrave: true },
    blk: { bezelHi: "#FFFFFF", bezelMid: "#AEBDCB", bezelLo: "#4E5A66", faceHi: "#3A4552", faceLo: "#141A21", glyph: "#EEF4FA", glyphLift: "#000000", rivet: "#D6E1EB", engrave: true },
  },
  {
    id: "maple",
    name: { zh: "秋楓", en: "Autumn Maple", vi: "Lá phong mùa thu" },
    note: "Late October: russet, amber, and a board the colour of a dry leaf.",
    board: { frameHi: "#D9A05A", frameLo: "#6B3A12", surfHi: "#E8BE86", surfLo: "#C0824E", line: "#7E421C", palace: "#95502A", river: "#A45E33", marker: "#7E421C" },
    red: { bezelHi: "#F6D9A8", bezelMid: "#B87A38", bezelLo: "#512C0E", faceHi: "#C4471F", faceLo: "#5A1608", glyph: "#FDE8C6", glyphLift: "#380D04", rivet: "#E3BA80", engrave: true },
    blk: { bezelHi: "#F6D9A8", bezelMid: "#B87A38", bezelLo: "#512C0E", faceHi: "#5A3A22", faceLo: "#22140B", glyph: "#F7E2C4", glyphLift: "#150A04", rivet: "#E3BA80", engrave: true },
  },
  {
    id: "inksea",
    name: { zh: "墨海", en: "Ink Sea", vi: "Biển mực" },
    note: "True black, for an OLED screen in a dark room. Nothing on the board glows except the characters.",
    board: { frameHi: "#3A3A3A", frameLo: "#000000", surfHi: "#0A0A0A", surfLo: "#000000", line: "#5A5A5A", palace: "#8A8A8A", river: "#7A7A7A", marker: "#5A5A5A" },
    red: { bezelHi: "#6E6E6E", bezelMid: "#2E2E2E", bezelLo: "#000000", faceHi: "#141414", faceLo: "#000000", glyph: "#FF5E4A", glyphLift: "#000000", rivet: "#4A4A4A", engrave: false },
    blk: { bezelHi: "#6E6E6E", bezelMid: "#2E2E2E", bezelLo: "#000000", faceHi: "#141414", faceLo: "#000000", glyph: "#FFFFFF", glyphLift: "#000000", rivet: "#4A4A4A", engrave: false },
  },
  {
    id: "celadon",
    name: { zh: "青瓷", en: "Celadon", vi: "Men ngọc" },
    note: "Longquan celadon: a pale green glaze with the crackle showing, and pieces glazed to match.",
    board: { frameHi: "#BFCBB4", frameLo: "#5E6B57", surfHi: "#DCE6D4", surfLo: "#B4C4AC", line: "#6E7F68", palace: "#566647", river: "#7E8F78", marker: "#6E7F68" },
    red: { bezelHi: "#EDF2E8", bezelMid: "#9EAE96", bezelLo: "#46523F", faceHi: "#F6F0E4", faceLo: "#D8CDB8", glyph: "#8E2C1E", glyphLift: "#FFFFFF", rivet: "#CFDAC6", engrave: true },
    blk: { bezelHi: "#EDF2E8", bezelMid: "#9EAE96", bezelLo: "#46523F", faceHi: "#C6D4BE", faceLo: "#8EA286", glyph: "#1E2C1A", glyphLift: "#FFFFFF", rivet: "#CFDAC6", engrave: true },
  },
  {
    id: "redcliff",
    name: { zh: "赤壁", en: "Red Cliff", vi: "Xích Bích" },
    note: "Dark basalt with an ember running through it. The board is stone; the red side is the fire.",
    board: { frameHi: "#8E6A4E", frameLo: "#2A1D14", surfHi: "#3A3230", surfLo: "#181412", line: "#B08054", palace: "#D0925E", river: "#C08A58", marker: "#B08054" },
    red: { bezelHi: "#E8B27A", bezelMid: "#95602E", bezelLo: "#3A2210", faceHi: "#C4441C", faceLo: "#4E1206", glyph: "#FFE2BE", glyphLift: "#2A0802", rivet: "#D0995E", engrave: false },
    blk: { bezelHi: "#E8B27A", bezelMid: "#95602E", bezelLo: "#3A2210", faceHi: "#2E2A28", faceLo: "#0C0A09", glyph: "#F0DCC4", glyphLift: "#000000", rivet: "#D0995E", engrave: false },
  },
  {
    id: "teahorse",
    name: { zh: "茶馬道", en: "Tea Horse Road", vi: "Trà Mã cổ đạo" },
    note: "Pressed tea brick and old saddle leather, off the caravan road into Yunnan.",
    board: { frameHi: "#A8804E", frameLo: "#4A3116", surfHi: "#7E5C3A", surfLo: "#4A3320", line: "#D9B885", palace: "#E8CDA0", river: "#E0C193", marker: "#D9B885" },
    red: { bezelHi: "#EFD3A6", bezelMid: "#A07A48", bezelLo: "#422C12", faceHi: "#A8402A", faceLo: "#421008", glyph: "#F8E2C2", glyphLift: "#2A0A04", rivet: "#D8BA8A", engrave: true },
    blk: { bezelHi: "#EFD3A6", bezelMid: "#A07A48", bezelLo: "#422C12", faceHi: "#4A3A2A", faceLo: "#1A120C", glyph: "#F2E0C6", glyphLift: "#0E0805", rivet: "#D8BA8A", engrave: true },
  },
  {
    id: "contrast",
    name: { zh: "高對比", en: "High Contrast", vi: "Tương phản cao" },
    note: "Not a decorative theme. Black on white at the strongest contrast the renderer can draw, for anyone who finds the wooden boards hard to read.",
    board: { frameHi: "#FFFFFF", frameLo: "#000000", surfHi: "#FFFFFF", surfLo: "#FFFFFF", line: "#000000", palace: "#000000", river: "#000000", marker: "#000000" },
    red: { bezelHi: "#FFFFFF", bezelMid: "#000000", bezelLo: "#000000", faceHi: "#FFFFFF", faceLo: "#FFFFFF", glyph: "#C20000", glyphLift: "#FFFFFF", rivet: "#000000", engrave: false },
    blk: { bezelHi: "#FFFFFF", bezelMid: "#000000", bezelLo: "#000000", faceHi: "#111111", faceLo: "#000000", glyph: "#FFFFFF", glyphLift: "#000000", rivet: "#FFFFFF", engrave: false },
  },
  {
    id: "colorsafe",
    name: { zh: "色弱友善", en: "Colour-safe", vi: "Cho người mù màu" },
    note: "Also not decorative. The sides are blue and amber rather than red and black, which stay apart under red-green colour blindness -- and the characters still carry the difference on their own.",
    board: { frameHi: "#C8CDD4", frameLo: "#5A6068", surfHi: "#F2F4F7", surfLo: "#DCE1E7", line: "#4A5058", palace: "#2E343A", river: "#6A7078", marker: "#4A5058" },
    red: { bezelHi: "#FFFFFF", bezelMid: "#9AA2AA", bezelLo: "#3A4048", faceHi: "#E89A2A", faceLo: "#8E5400", glyph: "#1A1000", glyphLift: "#FFD98A", rivet: "#D8DEE4", engrave: false },
    blk: { bezelHi: "#FFFFFF", bezelMid: "#9AA2AA", bezelLo: "#3A4048", faceHi: "#1E5AA8", faceLo: "#062E5E", glyph: "#EAF2FC", glyphLift: "#00142E", rivet: "#D8DEE4", engrave: false },
  },
];

/* The default is Sandalwood, and the photographic walnut board is the last
   choice rather than the first (2026-10-26). Player feedback on the walnut
   board kept coming back in the same words -- "strange", "lines of no use", "a
   row of squares down each side you cannot stand on": its grid fills only ~61%
   of the frame, so wide bare strips sit beside the outer files. A drawn board
   is ruled evenly, gives play ~84% of the space, and Sandalwood (light wood,
   cream pieces, red and black characters) is the look most players know.
   A player who chose walnut keeps it: only the default changed. */
const WALNUT: BoardTheme = {
    id: "walnut",
    name: { zh: "胡桃木 · 珍藏", en: "Walnut (collector)", vi: "Gỗ óc chó (sưu tầm)" },
    note: "The original photoreal walnut board with gold inlay and carved characters.",
    kind: "photo",
    fileX: PHOTO_FILE_X,
    rankY: PHOTO_RANK_Y,
    pr: 37,
    // The photo disc fills 0.915 of its PNG and the gold ring sits at ~0.80 of
    // that, so the usable face is ~54 units across at PR 37.
    fontSize: 50,
    gutL: 50,
    gutB: 54,
    goldGlyph: true,
};

const DRAWN_THEMES: BoardTheme[] = COLLECTION.map((t): BoardTheme => ({
    id: t.id,
    name: t.name,
    note: t.note,
    kind: "drawn",
    fileX: DRAWN.fileX,
    rankY: DRAWN.rankY,
    pr: DRAWN_PR,
    fontSize: DRAWN_FS,
    // 52, not 44/46: the coordinates sat against the frame's edge and read as clipped.
    gutL: 52,
    gutB: 52,
    board: t.board,
    red: t.red,
    blk: t.blk,
}));

const DEFAULT = "sandalwood";

export const BOARD_THEMES: BoardTheme[] = [
  ...DRAWN_THEMES.filter((t) => t.id === DEFAULT),
  ...DRAWN_THEMES.filter((t) => t.id !== DEFAULT),
  WALNUT,
];

const KEY = "xq-board-theme";

export function getBoardTheme(): BoardTheme {
  let id = DEFAULT;
  try { id = localStorage.getItem(KEY) || DEFAULT; } catch { /* storage unavailable */ }
  return BOARD_THEMES.find((t) => t.id === id) ?? BOARD_THEMES[0]!;
}

export function setBoardTheme(id: string): void {
  try { localStorage.setItem(KEY, id); } catch { /* storage unavailable */ }
}
